/**
 * @fileoverview Centralized error handling utilities
 * 
 * This module provides standardized error handling across the application.
 * It includes functionality for logging, tracking, and recovering from errors.
 */

import { toast } from '@/hooks/use-toast';

// Define error severity levels
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Standard error structure
export interface ApplicationError {
  message: string;
  code?: string | number;
  severity: ErrorSeverity;
  context?: Record<string, any>;
  timestamp: number;
  handled: boolean;
}

// Global error state
const errorState = {
  lastError: null as ApplicationError | null,
  errorCount: 0,
  errorLog: [] as ApplicationError[]
};

// Maximum number of errors to keep in memory
const MAX_ERROR_LOG_SIZE = 20;

/**
 * Create a standardized error object
 * @param message - Error message
 * @param severity - Error severity level
 * @param code - Error code (optional)
 * @param context - Additional error context (optional)
 * @returns Standardized error object
 */
export function createError(
  message: string,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  code?: string | number,
  context?: Record<string, any>
): ApplicationError {
  return {
    message,
    code,
    severity,
    context,
    timestamp: Date.now(),
    handled: false
  };
}

/**
 * Handle an error with consistent logging and user feedback
 * @param error - Error object or message
 * @param showToast - Whether to display a toast notification
 * @param context - Additional error context
 * @returns The standardized error object
 */
export function handleError(
  error: Error | string | ApplicationError,
  showToast = true,
  context?: Record<string, any>
): ApplicationError {
  let appError: ApplicationError;
  
  // Convert error to standard format
  if (typeof error === 'string') {
    appError = createError(error, ErrorSeverity.ERROR, undefined, context);
  } else if ('message' in error && typeof error.message === 'string') {
    // Regular Error object
    const errorObj = error as Error;
    appError = createError(
      errorObj.message,
      ErrorSeverity.ERROR,
      undefined,
      {
        ...(errorObj.stack ? { stack: errorObj.stack } : {}),
        ...context
      }
    );
  } else {
    // Already an ApplicationError
    appError = error as ApplicationError;
    
    // Add additional context if provided
    if (context) {
      appError.context = { ...appError.context, ...context };
    }
  }
  
  // Log error with appropriate severity
  logError(appError);
  
  // Show toast notification if requested
  if (showToast) {
    displayErrorToast(appError);
  }
  
  // Store error in state
  trackError(appError);
  
  // Mark as handled
  appError.handled = true;
  
  return appError;
}

/**
 * Log an error to the console with appropriate formatting
 * @param error - Error to log
 */
function logError(error: ApplicationError): void {
  const { message, code, severity, context, timestamp } = error;
  const logPrefix = `[${new Date(timestamp).toISOString()}]`;
  
  // Format message with code if available
  const formattedMessage = code ? `${message} (Code: ${code})` : message;
  
  // Use appropriate log level based on severity
  switch (severity) {
    case ErrorSeverity.INFO:
      console.info(`${logPrefix} ${formattedMessage}`, context);
      break;
    case ErrorSeverity.WARNING:
      console.warn(`${logPrefix} ${formattedMessage}`, context);
      break;
    case ErrorSeverity.ERROR:
    case ErrorSeverity.CRITICAL:
      console.error(`${logPrefix} ${formattedMessage}`, context);
      break;
    default:
      console.error(`${logPrefix} ${formattedMessage}`, context);
  }
}

/**
 * Show a toast notification for an error
 * @param error - Error to display
 */
function displayErrorToast(error: ApplicationError): void {
  const { message, severity } = error;
  
  switch (severity) {
    case ErrorSeverity.INFO:
      toast({ title: 'Information', description: message });
      break;
    case ErrorSeverity.WARNING:
      toast({ 
        title: 'Warning', 
        description: message,
        variant: 'destructive'
      });
      break;
    case ErrorSeverity.ERROR:
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      });
      break;
    case ErrorSeverity.CRITICAL:
      toast({
        title: 'Critical Error',
        description: `${message}. The application may not function correctly.`,
        variant: 'destructive',
        duration: 10000 // longer duration for critical errors
      });
      break;
  }
}

/**
 * Track an error in the application error state
 * @param error - Error to track
 */
function trackError(error: ApplicationError): void {
  // Update error state
  errorState.lastError = error;
  errorState.errorCount += 1;
  
  // Add to log with size limit
  errorState.errorLog.unshift(error);
  
  // Trim log if it exceeds maximum size
  if (errorState.errorLog.length > MAX_ERROR_LOG_SIZE) {
    errorState.errorLog = errorState.errorLog.slice(0, MAX_ERROR_LOG_SIZE);
  }
  
  // For critical errors, consider reporting to an error tracking service
  if (error.severity === ErrorSeverity.CRITICAL) {
    // In a production app, you would send this to your error tracking service
    // e.g., Sentry, LogRocket, etc.
  }
}

/**
 * Get the recent error log
 * @returns Array of recent errors
 */
export function getErrorLog(): ApplicationError[] {
  return [...errorState.errorLog];
}

/**
 * Get the error count
 * @returns Number of errors that have occurred
 */
export function getErrorCount(): number {
  return errorState.errorCount;
}

/**
 * Clear the error log
 */
export function clearErrorLog(): void {
  errorState.errorLog = [];
}

/**
 * Error handler for async operations
 * @param asyncFn - Async function to execute
 * @param errorMessage - Default error message
 * @returns A function that executes the async function with error handling
 */
export function withErrorHandling<T, Args extends any[]>(
  asyncFn: (...args: Args) => Promise<T>,
  errorMessage = 'An error occurred'
): (...args: Args) => Promise<T> {
  return async (...args: Args): Promise<T> => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleError(error instanceof Error ? error : errorMessage);
      throw error; // re-throw to allow caller to handle if needed
    }
  };
}

/**
 * Creates a boundary function for React error boundaries to call
 * @param componentName - Name of the component where the error occurred
 * @returns Function to handle errors in a React error boundary
 */
export function createErrorBoundaryHandler(componentName: string) {
  return (error: Error, errorInfo: React.ErrorInfo) => {
    const context = {
      componentStack: errorInfo.componentStack,
      component: componentName
    };
    
    handleError(error, true, context);
  };
}